/**
 * MCP Server for Canary CLI.
 *
 * Combines:
 *   - HTTP-only shared tools from @chatsdet/mcp-tools (workflows, runs, suites,
 *     credentials, issues, proposals)
 *   - CLI-local tools (login, upgrade, browser daemon, cloud-relay sessions)
 *     that depend on local state and can't be hosted remotely.
 *
 * @module
 */

import { Server } from '@modelcontextprotocol/sdk/server/index.js';
import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js';
import { CallToolRequestSchema, ListToolsRequestSchema } from '@modelcontextprotocol/sdk/types.js';
import { buildRegistry, sharedGroups } from '@chatsdet/mcp-tools';
import { readStoredToken } from '../auth';
import { readBrandEnv } from '../brand.js';
import { resolveApiUrl } from './api-url';
import { toolText } from './tool-helpers';
import { getSessionToolDefinitions, handleSessionTool } from './session-tools';
import { getBrowserMCPToolDefinitions, handleBrowserTool } from './browser-tools';
import { getLoginToolDefinitions, handleLoginTool } from './login-tool';
import { getUpgradeToolDefinitions, handleUpgradeTool } from './upgrade-tool';
import { CANARY_MCP_INSTRUCTIONS } from './instructions';

async function resolveCtx() {
  const apiToken = readBrandEnv('CANARY_API_TOKEN') ?? (await readStoredToken());
  if (!apiToken) {
    throw new Error('Missing token. Call the canary_login tool or set CANARY_API_TOKEN.');
  }
  return { apiUrl: resolveApiUrl(), apiToken };
}

export async function runMcp(_argv: string[]) {
  const registry = buildRegistry(sharedGroups);

  const server = new Server(
    { name: 'canary-cli', version: '0.1.0' },
    {
      capabilities: { tools: {} },
      instructions: CANARY_MCP_INSTRUCTIONS,
    }
  );

  server.setRequestHandler(ListToolsRequestSchema, async () => ({
    tools: [
      ...getLoginToolDefinitions(),
      ...getUpgradeToolDefinitions(),
      ...getSessionToolDefinitions(),
      ...getBrowserMCPToolDefinitions(),
      ...registry.list(),
    ],
  }));

  server.setRequestHandler(CallToolRequestSchema, async (req) => {
    const tool = req.params.name;
    const args = (req.params.arguments ?? {}) as Record<string, unknown>;

    // CLI-local groups try first; each returns null if the tool is not theirs.
    const loginResult = await handleLoginTool(tool, args);
    if (loginResult) return loginResult;

    const upgradeResult = await handleUpgradeTool(tool, args);
    if (upgradeResult) return upgradeResult;

    const browserResult = await handleBrowserTool(tool, args);
    if (browserResult) return browserResult;

    const sessionResult = await handleSessionTool(tool, args);
    if (sessionResult) return sessionResult;

    // Shared HTTP-only tools — provide ctx for this call.
    try {
      const ctx = await resolveCtx();
      return await registry.call(tool, args, ctx);
    } catch (err) {
      return toolText(err instanceof Error ? err.message : String(err));
    }
  });

  const transport = new StdioServerTransport();
  await server.connect(transport);
  return new Promise<void>(() => undefined);
}
